import * as yup from 'yup';
import type {
  ConfigurationEntryDto,
  UpdateCustomSubscriptionConfigurationEntryRequest,
} from '@/redux/services/generate/configurations/customSubscriptionConfigurations';

const errorMessagePrefix = 'customSubscriptionConfiguration.validation.';

const defaultEntryFormTranslationKeys = {
  fromErrorMessage: `${errorMessagePrefix}fromMustBeGreaterThanZero`,
  toErrorMessage: `${errorMessagePrefix}toMustBeGreaterThanZero`,
  priceErrorMessage: `${errorMessagePrefix}priceMustBeGreaterThanZero`,
  toHigherThanFromErrorMessage: `${errorMessagePrefix}toCannotBeLowerThanFrom`,
  rangeCannotOverlapErrorMessage: `${errorMessagePrefix}rangeCannotOverlap`,
} as const;

export type EntryFormTranslationKeys = typeof defaultEntryFormTranslationKeys;

export interface EntryFormValidationContext {
  entriesToCompare: ConfigurationEntryDto[];
}

const hasOverlappingRange = (
  from: number,
  to: number,
  entries: ConfigurationEntryDto[],
): boolean =>
  entries.some(
    (existingEntry) => from <= existingEntry.to && existingEntry.from <= to,
  );

const isNotOverlapping = (
  value: UpdateCustomSubscriptionConfigurationEntryRequest,
  context: EntryFormValidationContext | undefined,
): boolean => {
  const { from, to } = value;

  if (!Number.isFinite(from) || !Number.isFinite(to)) {
    return true;
  }

  return !hasOverlappingRange(from, to, context?.entriesToCompare ?? []);
};

export const createEntryFormSchema = (
  keys: EntryFormTranslationKeys = defaultEntryFormTranslationKeys,
) =>
  yup.object({
    from: yup
      .number()
      .typeError(keys.fromErrorMessage)
      .required(keys.fromErrorMessage)
      .moreThan(0, keys.fromErrorMessage)
      .test('range-overlap-from', keys.rangeCannotOverlapErrorMessage, function () {
        return isNotOverlapping(
          this.parent as UpdateCustomSubscriptionConfigurationEntryRequest,
          this.options.context as EntryFormValidationContext | undefined,
        );
      }),
    to: yup
      .number()
      .typeError(keys.toErrorMessage)
      .required(keys.toErrorMessage)
      .moreThan(0, keys.toErrorMessage)
      .test(
        'to-lower-than-from',
        keys.toHigherThanFromErrorMessage,
        function (toValue) {
          const { from } = this
            .parent as UpdateCustomSubscriptionConfigurationEntryRequest;

          if (toValue === undefined || from === undefined) {
            return true;
          }

          return toValue >= from;
        },
      )
      .test('range-overlap-to', keys.rangeCannotOverlapErrorMessage, function () {
        return isNotOverlapping(
          this.parent as UpdateCustomSubscriptionConfigurationEntryRequest,
          this.options.context as EntryFormValidationContext | undefined,
        );
      }),
    pricePerUnit: yup
      .number()
      .typeError(keys.priceErrorMessage)
      .required(keys.priceErrorMessage)
      .moreThan(0, keys.priceErrorMessage),
  }) as yup.ObjectSchema<UpdateCustomSubscriptionConfigurationEntryRequest>;
